import { get } from 'lodash';

import {
  Actions as CareerHistoryActions,
  type CareerHistoryAction,
} from 'src/components/Profile/CareerHistory/actions';
import {
  getConfirmedRolesFromCache,
  getUnconfirmedRolesFromCache,
} from 'src/components/Profile/CareerHistory/data';
import type { Analytics } from 'src/middlewares/createAnalyticsMiddleware';
import type {
  ConfirmedActionName,
  UnconfirmedActionName,
} from 'src/modules/analytics/types';
import type { AppConfig } from 'src/modules/appConfig/types';
import type { ApiClient, ReduxInjections } from 'src/types';

import {
  instrumentAnalytics,
  applySuccessItemCheck,
} from '../analyticsMiddlewareUtils';

const currentPageSection = 'career history';

export const createCareerHistoryAnalyticsMiddleware = (
  analytics: Analytics,
  { apolloClient }: ApiClient,
  { showFeature }: AppConfig,
) =>
  instrumentAnalytics(analytics) &&
  (({ getState }: ReduxInjections) =>
    (next: (action: CareerHistoryAction) => void) =>
    (action: CareerHistoryAction) => {
      const { updateUnconfirmedCard, updateCard } = analytics;

      switch (action.type) {
        case CareerHistoryActions.UPDATE_CAREER_HISTORY_START: {
          const { actionName, payload } = action;
          const { id, achievements } = payload;
          const { confirmedRoles } = getConfirmedRolesFromCache(apolloClient);
          const { unconfirmedRoles } = getUnconfirmedRolesFromCache(
            apolloClient,
            showFeature('resume-parsing'),
          );
          const existingRole = [...confirmedRoles, ...unconfirmedRoles].find(
            (role) => role.id === id,
          );

          const existingRoleAchievements = Boolean(existingRole?.achievements);

          const updatedAchievements = Boolean(achievements?.length);

          const isDescriptionAdded = Boolean(
            existingRoleAchievements === false && updatedAchievements === true,
          );

          if (actionName) {
            updateCard({
              actionName: actionName as ConfirmedActionName,
              currentPageSection,
              customProps: {
                isDescriptionAdded,
              },
            });
          }

          break;
        }

        case CareerHistoryActions.DELETE_CAREER_HISTORY_START: {
          const { actionName } = action;

          if (actionName) {
            updateCard({
              actionName: 'DELETE',
              currentPageSection,
            });
          }

          break;
        }

        case CareerHistoryActions.ADD_UNCONFIRMED_ROLE_START:
        case CareerHistoryActions.REJECT_UNCONFIRMED_ROLE_START: {
          const { actionName, payload, meta } = action;

          const contentRead = get(meta, 'analytics.contentRead');

          const { id: roleId } = payload;
          const isApplySuccessEdit = applySuccessItemCheck(getState, roleId);
          const actionOrigin = isApplySuccessEdit
            ? 'application form'
            : 'resume parsing nudge';

          if (actionName) {
            updateUnconfirmedCard({
              actionName: actionName as UnconfirmedActionName,
              contentRead,
              actionOrigin,
              currentPageSection,
            });
          }
          break;
        }

        default: {
          break;
        }
      }
      return next(action);
    });
